import { Archive, History, Pencil } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  useArchiveMaxwellThread,
  useMaxwellThreads,
  useRenameMaxwellThread,
} from "@/hooks/useMaxwell";
import { formatDateTime } from "@/lib/format";
import type { MaxwellSubject } from "@/types/maxwell";

/**
 * Thread-history popover for the Maxwell panel. Lists the caller's
 * non-archived threads for the current subject; each row switches to that
 * thread, with rename + archive affordances.
 */
export function MaxwellThreadHistory({
  subject,
  activeThreadId,
  onSwitch,
}: {
  subject: MaxwellSubject;
  activeThreadId: string | undefined;
  onSwitch: (threadId: string) => void;
}) {
  const { data: threads } = useMaxwellThreads(subject);
  const rename = useRenameMaxwellThread(subject);
  const archive = useArchiveMaxwellThread(subject);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" title="History">
          <History size={14} />
          History
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-1">
        <ul className="flex max-h-80 flex-col gap-0.5 overflow-y-auto">
          {(threads ?? []).map((t) => (
            <li key={t.thread_id}>
              {editing === t.thread_id ? (
                <form
                  className="flex items-center gap-1 p-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const title = draft.trim();
                    if (title) {
                      rename.mutate({ threadId: t.thread_id, title });
                    }
                    setEditing(null);
                  }}
                >
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    autoFocus
                  />
                  <Button type="submit" size="sm">
                    Save
                  </Button>
                </form>
              ) : (
                <div
                  className={`flex items-center gap-1 rounded-md px-2 py-1.5 hover:bg-row-hover ${
                    t.thread_id === activeThreadId ? "bg-row-hover" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 flex-col text-left"
                    onClick={() => onSwitch(t.thread_id)}
                  >
                    <span className="truncate text-sm text-foreground">
                      {t.title ?? "Untitled chat"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(t.updated_at)}
                    </span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title="Rename"
                    onClick={() => {
                      setEditing(t.thread_id);
                      setDraft(t.title ?? "");
                    }}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title="Archive"
                    onClick={() => archive.mutate(t.thread_id)}
                  >
                    <Archive size={14} />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
